/**
 * Difficulté du **mode illimité**, côté client (DECISIONS §7.6, arbitrage A-25, défaut D-M3-05).
 *
 * Le serveur publie, dans la composition de chaque vague (`wave.difficulte`), le facteur
 * **cumulatif** à appliquer : `1` en mode limité, `multiplicateur_difficulte_illimite ^ round` en
 * mode illimité. Le client ne le recalcule **jamais** à partir de `balance` : le serveur reste la
 * source de vérité (A-35), et le facteur publié porte déjà le cumul — le réélever à la puissance
 * côté client doublerait la difficulté au round 3.
 *
 * Ce module est volontairement pur (aucun import de Phaser, aucun accès réseau) et fait trois
 * choses, pas une de plus :
 * 1. **résoudre** le facteur applicable à une composition publiée, avec une **erreur nommée**
 *    quand le mode illimité aurait dû le publier (aucun repli silencieux) ;
 * 2. **valider** un facteur reçu (`> 0`, fini) ;
 * 3. **appliquer** la règle §7.6 : PV **et** vitesse × facteur, et rien d'autre. Les éclats gagnés
 *    et les PV perdus du parcours ne dépendent pas de la difficulté (A-35, §8.3) : ils restent
 *    lus dans le contenu publié, inchangés.
 *
 * Il est le pendant client de `statsEnnemiPourDifficulte` (`apps/api/src/runs/moteur.ts`) : le
 * client ne peut pas importer un module de l'API, et la règle doit rester vérifiable des deux
 * côtés du contrat.
 */
import type { Ennemi } from '@elemora/shared/content';

/**
 * Le serveur n'a pas publié `wave.difficulte` alors que le mode illimité l'exige (round ≥ 2).
 *
 * Erreur **nommée** et non repli à `1` : jouer le round 2 avec les statistiques du round 1 est
 * exactement le défaut D-M3-05. Mieux vaut ne pas lancer la vague et le signaler que la jouer
 * faussée en silence.
 */
export class DifficulteManquanteError extends Error {
  /** Index **global** de la vague dans la run (0-based). */
  readonly indexVague: number;
  /** Round 1-based déduit de l'index (`floor(index / vaguesParRound) + 1`). */
  readonly round: number;

  constructor(indexVague: number, round: number) {
    super(
      `Difficulté absente de la composition de la vague ${String(indexVague)} ` +
        `(round ${String(round)} en mode illimité) : le serveur doit publier « wave.difficulte ».`,
    );
    this.name = 'DifficulteManquanteError';
    this.indexVague = indexVague;
    this.round = round;
  }
}

/** Le facteur publié n'est pas un multiplicateur valide (`NaN`, `Infinity`, `0`, négatif). */
export class DifficulteInvalideError extends Error {
  /** Valeur refusée, telle que reçue (`NaN`/`Infinity` inclus). */
  readonly valeur: number;

  constructor(valeur: number) {
    super(
      `Difficulté publiée invalide : ${String(valeur)} (un multiplicateur strictement positif est attendu).`,
    );
    this.name = 'DifficulteInvalideError';
    this.valeur = valeur;
  }
}

/** Contexte de mode nécessaire pour décider ce que vaut une difficulté absente. */
export interface ContexteDifficulte {
  /** `true` pour une run du gabarit `niv_illimite` (mode lu dans le contenu publié). */
  readonly illimite: boolean;
  /** Round 1-based de la vague, déduit de l'index global par l'appelant. */
  readonly round: number;
  /** Index global de la vague, recopié dans l'erreur nommée. */
  readonly indexVague: number;
}

/**
 * Valide un facteur de difficulté : fini et strictement positif.
 *
 * Aucune valeur de repli n'est renvoyée : un facteur invalide est une erreur de programmation ou
 * un contrat violé, jamais un `1` silencieux.
 */
export function validerFacteurDifficulte(valeur: number): number {
  if (!Number.isFinite(valeur) || valeur <= 0) throw new DifficulteInvalideError(valeur);
  return valeur;
}

/**
 * Facteur applicable à une composition de vague publiée par le serveur.
 *
 * - champ présent → il est validé puis renvoyé **tel quel** (déjà cumulé par le serveur) ;
 * - champ absent, mode limité → `1` (le champ est optionnel au contrat pour ces vagues) ;
 * - champ absent, mode illimité **round 1** → `1` : les compositions illimitées enregistrées avant
 *   M3-05 n'ont pas le champ, et leur round 1 vaut effectivement `1` (aucune difficulté ajoutée) ;
 * - champ absent, mode illimité **round ≥ 2** → `DifficulteManquanteError` (aucun repli).
 */
export function resoudreDifficulte(
  vague: { readonly difficulte?: number | undefined },
  contexte: ContexteDifficulte,
): number {
  const publiee = vague.difficulte;
  if (publiee !== undefined) return validerFacteurDifficulte(publiee);
  if (!contexte.illimite || contexte.round <= 1) return 1;
  throw new DifficulteManquanteError(contexte.indexVague, contexte.round);
}

/**
 * PV et vitesse **effectifs** d'un ennemi à une difficulté donnée : `valeur × facteur` (§7.6).
 *
 * Seules ces deux statistiques dépendent de la difficulté. La récompense en éclats
 * (`recompenseEclats`), les PV perdus du parcours (`pvParcoursPerdu`), l'armure et le rayon sont
 * recopiés du contenu sans facteur.
 */
export function appliquerDifficulte(
  ennemi: Pick<Ennemi, 'pv' | 'vitesse'>,
  facteur: number,
): { pv: number; vitesse: number } {
  const valide = validerFacteurDifficulte(facteur);
  return { pv: ennemi.pv * valide, vitesse: ennemi.vitesse * valide };
}
